import { useRef } from 'react';
import { Goal, ProofState as ProofStateType } from '../../types/proof';
import { AlertCircle, CheckCircle2, Info, Target } from 'lucide-react';
import { PanelResizeHandle, type PanelSize } from '../common/PanelResizeHandle';

interface ProofOutputProps {
  state: ProofStateType | null;
  isExecuting?: boolean;
  initializationMessage?: string;
  onResize: (size: PanelSize) => void;
}

// Format goal in jsCoq style
function formatGoal(goal: Goal): string {
  const goalType = goal.type || '';
  const context = goal.context || [];
  let output = "";
  
  // Add hypotheses/context
  if (context.length > 0) {
    context.forEach(hyp => {
      output += `${hyp.name} : ${hyp.type}\n`;
    });
    // Add separator  line
    output += '========\n\n';
  }
  
  // Add goal type
  output += goalType;
  
  return output;
}

export function ProofOutput({ state, isExecuting, initializationMessage, onResize }: ProofOutputProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const remainingGoals = (state?.goals.length ?? 0) + (state?.unfocusedGoalCount ?? 0);
  const progress = state?.subgoalProgress;
  return (
    <div ref={panelRef} className="relative flex h-full min-h-0 flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="shrink-0 bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600 flex items-center space-x-2">
        <Target size={16} className="text-gray-600 dark:text-gray-300" />
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Proof State</h3>
        {isExecuting && (
          <span className="ml-auto text-xs text-primary-600 dark:text-primary-400 animate-pulse">Executing...</span>
        )}
      </div>
      
      <div className="min-h-0 flex-1 p-4 pb-8 space-y-4 overflow-auto">
        {!state && (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-8">
            {initializationMessage || 'Start writing your proof to see the current state here'}
          </div>
        )}

        {state && (
          <>
            {state.isComplete && !state.hasError ? (
              <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 size={20} />
                  <span className="font-semibold">{state.provedTheorem ? 'Theorem proven!' : 'Proof Complete!'}</span>
                </div>
                <p className="mt-2 text-sm">
                  {state.provedTheorem
                    ? `${state.provedTheorem} has been checked and saved by Rocq.`
                    : 'All goals have been proven. Great work!'}
                </p>
              </div>
            ) : (
              <>
                {state.goals.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Target size={16} className="text-primary-600 dark:text-primary-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {state.goals.length > 1 ? `Goals (${state.goals.length})` : 'Current goal'}
                      </h4>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{remainingGoals} remaining</span>
                    </div>
                    <div className="space-y-3">
                      {state.goals.map((goal, index) => (
                        <div key={goal.id} className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded p-4">
                          {state.goals.length > 1 && <p className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Subgoal {index + 1}</p>}
                          <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                            {formatGoal(goal)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {state.goals.length === 0 && !state.hasError && progress && progress.total > 0 && (
                  <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                    <div className="flex items-center gap-2 font-semibold"><CheckCircle2 size={18} aria-hidden="true" />{remainingGoals > 0 ? 'Subgoal complete' : 'All subgoals solved'}</div>
                    <p className="mt-2 text-sm">
                      {progress.completed} of {progress.total} subgoals complete. {remainingGoals} remaining.
                    </p>
                    <p className="mt-2 text-sm">{remainingGoals > 0 ? 'Continue with the next bullet.' : 'Finish the proof with Qed.'}</p>
                  </div>
                )}
                {state.goals.length === 0 && !state.hasError && (!progress || progress.total === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No active goals.</p>
                )}
              </>
            )}

            {state.messages.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Info size={16} className="text-gray-600 dark:text-gray-300" />
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Messages</h4>
                </div>
                <div className="space-y-2">
                  {state.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start space-x-2 p-3 rounded ${
                        msg.level === 'error'
                          ? 'bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300'
                          : msg.level === 'warning'
                          ? 'bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-800 text-warning-700 dark:text-warning-300'
                          : 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      {msg.level === 'error' ? (
                        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                      ) : (
                        <Info size={16} className="flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <pre className="text-sm whitespace-pre-wrap font-mono">{msg.text}</pre>
                        {msg.location && (
                          <div className="text-xs mt-1 opacity-75">
                            Line {msg.location.line}, Column {msg.location.column}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <PanelResizeHandle label="Resize proof state" panelRef={panelRef} onResize={onResize} corner="left" />
    </div>
  );
}
